import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  IconButton,
  Link,
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { UsersThree, X } from "@phosphor-icons/react";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { formatToPhoneNumber } from "utils/formatFormFields";
import { COUNTRIES, STATES, TIMEZONES, LANGUAGES } from "@constants";
import { TeamRole } from "./interface";

interface AddTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddTeamMemberFormValues) => void;
  teamRoles: TeamRole[];
  existingEmails: string[];
  clientName?: string;
  isSubmitting?: boolean;
  userPermissions?: {
    create_phi_release_request?: boolean;
  };
}

export interface AddTeamMemberFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  relationshipType: number | string;
  isDesignatedCaregiver: boolean;
  isLegalGuardian: boolean;
  dateOfBirth: Date | null;
  gender: string;
  preferredLanguage: string;
  address1: string;
  address2: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  timezone: string;
  sendInvitation: boolean;
  generateRoi: boolean;
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  phoneNumber: yup.string().required("Phone Number is required"),
  email: yup.string().email("Invalid email address").required("Email is required"),
  relationshipType: yup.mixed().required("Relationship is required"),
  dateOfBirth: yup.date().nullable(),
  gender: yup.string(),
  preferredLanguage: yup.string(),
  address1: yup.string(),
  address2: yup.string(),
  country: yup.string(),
  state: yup.string(),
  city: yup.string(),
  zipCode: yup.string(),
  timezone: yup.string(),
});

const initialValues: AddTeamMemberFormValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  relationshipType: "",
  isDesignatedCaregiver: false,
  isLegalGuardian: false,
  dateOfBirth: null,
  gender: "",
  preferredLanguage: "",
  address1: "",
  address2: "",
  country: "",
  state: "",
  city: "",
  zipCode: "",
  timezone: "",
  sendInvitation: true,
  generateRoi: true,
};

/**
 * Dialog for adding a new team member to a client's care team.
 * Collects required contact info, relationship, and optional demographic/address fields.
 */
const AddTeamMemberDialog: React.FC<AddTeamMemberDialogProps> = ({
  open,
  onClose,
  onSubmit,
  teamRoles,
  existingEmails,
  clientName,
  isSubmitting = false,
  userPermissions,
}) => {
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [roiValueOverridden, setRoiValueOverridden] = useState(false);
  const canCreateRoi = Boolean(userPermissions?.create_phi_release_request);

  const formik = useFormik<AddTeamMemberFormValues>({
    initialValues,
    validationSchema: validationSchema.shape({
      email: yup
        .string()
        .email("Invalid email address")
        .required("Email is required")
        .test("is-unique", "Member already added", (val) => {
          if (!val) return true;
          const normalized = val.trim().toLowerCase();
          return !existingEmails.some((e) => e?.trim().toLowerCase() === normalized);
        }),
    }),
    onSubmit: (values) => {
      const isRoiEligible = !values.isDesignatedCaregiver && !values.isLegalGuardian;
      onSubmit({
        ...values,
        generateRoi: canCreateRoi && isRoiEligible && values.generateRoi,
      });
    },
    validateOnMount: true,
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setShowAdditionalFields(false);
      setRoiValueOverridden(false);
    }
  }, [open]);

  useEffect(() => {
    const isRoiEligible = !formik.values.isDesignatedCaregiver && !formik.values.isLegalGuardian;

    if (!canCreateRoi || !isRoiEligible) {
      if (formik.values.generateRoi) {
        formik.setFieldValue("generateRoi", false, false);
      }
      return;
    }

    if (roiValueOverridden) {
      return;
    }

    if (!formik.values.generateRoi) {
      formik.setFieldValue("generateRoi", true, false);
    }
  }, [
    formik.values.generateRoi,
    formik.values.isDesignatedCaregiver,
    formik.values.isLegalGuardian,
    roiValueOverridden,
    canCreateRoi,
  ]);

  const handleClose = () => {
    if (!isSubmitting) {
      formik.resetForm();
      setShowAdditionalFields(false);
      setRoiValueOverridden(false);
      onClose();
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value.replace(/[^\d]/g, "");
    formik.handleChange({ target: { name: "phoneNumber", value: phone } });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="add-team-member-dialog-title"
    >
      <DialogTitle
        id="add-team-member-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={3}>
          <UsersThree weight="duotone" size={24} />
          <Typography variant="h4" component="span">
            Add Team Member
          </Typography>
        </Stack>
        <IconButton onClick={handleClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form id="add-team-member-form" onSubmit={formik.handleSubmit}>
          <Grid container rowSpacing={5}>
            {/* Required Fields */}
            <Grid xs={12}>
              <TextField
                fullWidth
                label="First Name (required)"
                name="firstName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                error={formik.touched.firstName && !!formik.errors.firstName}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                fullWidth
                label="Last Name (required)"
                name="lastName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                error={formik.touched.lastName && !!formik.errors.lastName}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                fullWidth
                label="Phone Number (required)"
                name="phoneNumber"
                onChange={handlePhoneNumberChange}
                onBlur={formik.handleBlur}
                value={formatToPhoneNumber(formik.values.phoneNumber)}
                error={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                fullWidth
                label="Email Address (required)"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.touched.email && !!formik.errors.email}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                select
                fullWidth
                label="Relationship to Client (required)"
                name="relationshipType"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.relationshipType}
                error={formik.touched.relationshipType && !!formik.errors.relationshipType}
                helperText={formik.touched.relationshipType && formik.errors.relationshipType}
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                {teamRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Checkboxes */}
            <Grid xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isDesignatedCaregiver"
                    checked={formik.values.isDesignatedCaregiver}
                    onChange={formik.handleChange}
                  />
                }
                label="Designated Caregiver"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 8 }}>
                Caregivers get all the updates and messages while the clients are in care.
              </Typography>
            </Grid>

            <Grid xs={12} sx={{ pt: "0 !important" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isLegalGuardian"
                    checked={formik.values.isLegalGuardian}
                    onChange={formik.handleChange}
                  />
                }
                label="Legal Guardian"
              />
            </Grid>

            {/* When expanded: link first, then fields, then invitation */}
            {showAdditionalFields && (
              <>
                <Grid xs={12}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setShowAdditionalFields(false)}
                    sx={{ cursor: "pointer" }}
                  >
                    Hide additional fields
                  </Link>
                </Grid>

                <Grid xs={12}>
                  <ANDatePicker
                    label="Date of Birth"
                    format="MM/dd/yyyy"
                    disableFuture
                    name="dateOfBirth"
                    value={formik.values.dateOfBirth}
                    onChange={(value: Date | null) => {
                      formik.handleChange({ target: { name: "dateOfBirth", value } });
                    }}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "MM/DD/YYYY",
                        error: formik.touched.dateOfBirth && !!formik.errors.dateOfBirth,
                        helperText: formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                      },
                      popper: {
                        disablePortal: false,
                        sx: { zIndex: 1400 },
                      },
                    }}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    name="gender"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.gender}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Non-binary">Non-binary</MenuItem>
                    <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
                    <MenuItem value="Unknown">-</MenuItem>
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Preferred Language"
                    name="preferredLanguage"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.preferredLanguage}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {LANGUAGES.map((lang: string) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Address 1"
                    name="address1"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address1}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    name="address2"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address2}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Country"
                    name="country"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.country}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(COUNTRIES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="State"
                    name="state"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.state}
                    disabled={formik.values.country !== "US"}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(STATES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    name="zipCode"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.zipCode}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Timezone"
                    name="timezone"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.timezone}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(TIMEZONES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {/* Send invitation checkbox */}
            <Grid xs={12}>
              <Box
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  py: 5,
                  px: 6,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sendInvitation"
                      checked={formik.values.sendInvitation}
                      onChange={formik.handleChange}
                      sx={{
                        color: "primary.main",
                        "&.Mui-checked": { color: "primary.main" },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Send account invitation to this email address
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 8 }}>
                  They&apos;ll receive an email to create an AnswersNow account and access{" "}
                  {clientName ? `${clientName}'s` : "the client's"} care information.
                </Typography>
              </Box>
            </Grid>

            {/* Generate ROI checkbox */}
            {canCreateRoi && (
              <Grid xs={12}>
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    borderRadius: 2,
                    py: 5,
                    px: 6,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="generateRoi"
                        checked={formik.values.generateRoi}
                        disabled={
                          formik.values.isDesignatedCaregiver || formik.values.isLegalGuardian
                        }
                        onChange={(event) => {
                          setRoiValueOverridden(true);
                          formik.handleChange(event);
                        }}
                        sx={{
                          color: "primary.main",
                          "&.Mui-checked": { color: "primary.main" },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Generate Release of Information form
                      </Typography>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 8 }}>
                    Generate and send the Release of Information consent form to the designated
                    caregiver for this team member
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* When collapsed: link after invitation */}
            {!showAdditionalFields && (
              <Grid xs={12}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setShowAdditionalFields(true)}
                  sx={{ cursor: "pointer" }}
                >
                  Show additional fields
                </Link>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 6, pb: 5 }}>
        <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          form="add-team-member-form"
          variant="contained"
          loading={isSubmitting}
          disabled={!formik.isValid || !formik.dirty}
        >
          {formik.values.sendInvitation ? "Add Team Member & Send Invite" : "Add Team Member"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
